import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, mediaUrl, LOGO } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Field, LocalizedField, Uploader, SingleUpload } from "@/components/admin/Fields";
import { Trash2, Plus, Save, LogOut, ArrowLeft, GripVertical } from "lucide-react";

const TABS = [
  ["content", "Site İçeriği"], ["products", "Ürünler"], ["catalogs", "Kataloglar"],
  ["users", "Katalog Kullanıcıları"], ["fairs", "Fuarlar"], ["news", "Haberler"],
  ["registrations", "Kayıt Talepleri"],
];

const btn = "bg-gold text-black px-6 py-2.5 text-[0.68rem] uppercase tracking-[0.2em] font-medium hover:bg-white transition-colors duration-300 inline-flex items-center gap-2";
const btnGhost = "border border-white/15 text-neutral-300 px-5 py-2.5 text-[0.66rem] uppercase tracking-[0.2em] hover:bg-white/10 transition-colors duration-300 inline-flex items-center gap-2";
const card = "border border-white/8 bg-[#0c0c0c] p-6";

function Login() {
  const { login } = useApp();
  const [form, setForm] = useState({ username: "", password: "" });
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await api.post("/auth/login", form);
      if (data.user.role !== "admin") { setError("Bu hesap yönetici değil"); return; }
      login(data.token, data.user);
    } catch (er) { setError(errMsg(er)); }
  };

  return (
    <div className="min-h-screen bg-ink flex items-center justify-center px-6" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-md backdrop-blur-xl bg-black/60 border border-white/10 p-10">
        <img src={LOGO} alt="BEDAS" className="h-14 w-auto object-contain logo-light mb-8" />
        <p className="overline mb-8">Yönetim Paneli</p>
        <div className="mb-5">
          <label className="block text-[0.62rem] uppercase tracking-[0.2em] text-neutral-500 mb-2">E-posta</label>
          <input required data-testid="admin-username" value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
            className="w-full bg-[#131313] border border-white/10 px-3 py-3 text-sm text-white outline-none focus:border-gold" />
        </div>
        <div className="mb-7">
          <label className="block text-[0.62rem] uppercase tracking-[0.2em] text-neutral-500 mb-2">Şifre</label>
          <input required type="password" data-testid="admin-password" value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="w-full bg-[#131313] border border-white/10 px-3 py-3 text-sm text-white outline-none focus:border-gold" />
        </div>
        {error && <p data-testid="admin-login-error" className="text-xs text-red-400 mb-4">{error}</p>}
        <button type="submit" data-testid="admin-login-btn" className={`${btn} w-full justify-center`}>Giriş Yap</button>
        <Link to="/" className="block mt-6 text-center text-[0.66rem] uppercase tracking-[0.2em] text-neutral-500 hover:text-gold">Siteye Dön</Link>
      </form>
    </div>
  );
}

function ContentTab() {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/content").then((r) => setC(r.data)); }, []);
  const save = async () => {
    try { await api.put("/admin/content", c); toast.success("İçerik kaydedildi"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!c) return <p className="text-sm text-neutral-500">Yükleniyor...</p>;
  const set = (k, v) => setC({ ...c, [k]: v });

  return (
    <div className="flex flex-col gap-8" data-testid="tab-content">
      <div className={card}>
        <p className="overline mb-5">Anasayfa Hero</p>
        <div className="flex flex-col gap-5">
          <LocalizedField label="Başlık" textarea={false} testId="hero-title"
            value={c.hero?.title} onChange={(v) => set("hero", { ...c.hero, title: v })} />
          <LocalizedField label="Alt Başlık" testId="hero-subtitle"
            value={c.hero?.subtitle} onChange={(v) => set("hero", { ...c.hero, subtitle: v })} />
          <SingleUpload label="Hero Görseli" accept="image/*" testId="hero-image"
            value={c.hero?.image} onChange={(v) => set("hero", { ...c.hero, image: v })} />
        </div>
      </div>

      <div className={card}>
        <p className="overline mb-5">Sayfa Kapak Görselleri</p>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {[["products", "Ürünler Kapak"], ["fairs", "Fuarlar Kapak"], ["news", "Haberler Kapak"],
            ["about", "Hakkımızda Kapak"], ["contact", "İletişim Görseli"]].map(([k, label]) => (
            <SingleUpload key={k} label={label} accept="image/*" testId={`cover-${k}`}
              value={c.covers?.[k]} onChange={(v) => set("covers", { ...c.covers, [k]: v })} />
          ))}
        </div>
      </div>

      {[["about", "Hakkımızda"], ["vision", "Vizyonumuz"], ["mission", "Misyonumuz"]].map(([k, label]) => (
        <div className={card} key={k}>
          <p className="overline mb-5">{label}</p>
          <LocalizedField label={label} testId={`content-${k}`} value={c[k]} onChange={(v) => set(k, v)} />
        </div>
      ))}

      <div className={card}>
        <p className="overline mb-5">İletişim</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {[["address", "Adres"], ["phone", "Telefon"], ["email", "E-posta"], ["whatsapp", "WhatsApp"], ["instagram", "Instagram"]].map(([k, l]) => (
            <Field key={k} label={l} testId={`contact-${k}`} value={c.contact?.[k]}
              onChange={(v) => set("contact", { ...c.contact, [k]: v })} />
          ))}
        </div>
      </div>

      <button onClick={save} data-testid="save-content-btn" className={`${btn} self-start`}><Save size={14} /> Kaydet</button>
    </div>
  );
}

function ProductsTab() {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const load = () => api.get("/products").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const body = { ...draft, images: (draft.media || []).map((m) => m.url) };
      delete body.media; delete body.id; delete body.created_at; delete body.is_deleted;
      if (draft.id) await api.put(`/admin/products/${draft.id}`, body);
      else await api.post("/admin/products", { ...body, order: items.length });
      toast.success("Ürün kaydedildi"); setDraft(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => { await api.delete(`/admin/products/${id}`); load(); toast.success("Silindi"); };

  const drop = async (target) => {
    if (dragIdx === null || dragIdx === target) { setDragIdx(null); return; }
    const next = [...items];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(target, 0, moved);
    setItems(next);
    setDragIdx(null);
    try {
      await api.post("/admin/products-order", { ids: next.map((p) => p.id) });
      toast.success("Sıralama kaydedildi");
    } catch (e) { toast.error(errMsg(e)); load(); }
  };

  return (
    <div data-testid="tab-products">
      <button className={btn} data-testid="new-product-btn"
        onClick={() => setDraft({ name: "", category: "yatak", description: { tr: "", en: "", de: "" }, media: [] })}>
        <Plus size={14} /> Yeni Ürün
      </button>

      {draft && (
        <div className={`${card} mt-6 flex flex-col gap-5`} data-testid="product-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Ürün Adı" testId="product-name" value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} />
            <Field label="Kategori (yatak / baza / başlık)" testId="product-category" value={draft.category} onChange={(v) => setDraft({ ...draft, category: v })} />
          </div>
          <LocalizedField label="Açıklama" testId="product-desc" value={draft.description} onChange={(v) => setDraft({ ...draft, description: v })} />
          <Uploader label="Ürün Fotoğrafları" accept="image/*" testId="product-images"
            items={draft.media} onChange={(m) => setDraft({ ...draft, media: m })} />
          <div className="flex gap-3">
            <button onClick={save} className={btn} data-testid="save-product-btn"><Save size={14} /> Kaydet</button>
            <button onClick={() => setDraft(null)} className={btnGhost}>İptal</button>
          </div>
        </div>
      )}

      <p className="mt-8 text-xs text-neutral-500">Kartları sürükleyip bırakarak vitrindeki sırayı belirleyebilirsiniz.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 mt-4">
        {items.map((p, i) => (
          <div key={p.id} className={`${card} cursor-grab active:cursor-grabbing ${dragIdx === i ? "opacity-40" : ""}`}
            data-testid={`admin-product-${i}`} draggable
            onDragStart={() => setDragIdx(i)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => drop(i)}>
            <div className="flex gap-4">
              <GripVertical size={16} className="text-neutral-600 shrink-0 mt-1" />
              {p.images?.[0] && <img src={mediaUrl(p.images[0])} alt="" className="w-20 h-20 object-cover border border-white/10" />}
              <div className="flex-1">
                <p className="text-[0.6rem] uppercase tracking-[0.2em] text-gold">{p.category}</p>
                <p className="font-serif text-xl text-white">{p.name}</p>
                <p className="text-[0.6rem] uppercase tracking-[0.2em] text-neutral-600 mt-1">Sıra {i + 1}</p>
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button className={btnGhost} data-testid={`edit-product-${i}`}
                onClick={() => setDraft({ ...p, media: (p.images || []).map((u) => ({ type: "image", url: u })) })}>Düzenle</button>
              <button className={btnGhost} data-testid={`delete-product-${i}`} onClick={() => del(p.id)}><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MediaCollectionTab({ name, titleField, label }) {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState(null);
  const load = () => api.get(`/${name}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [name]);

  const blank = () => ({ [titleField]: "", date: "", ...(name === "fairs" ? { location: "" } : {}),
    [name === "fairs" ? "description" : "content"]: { tr: "", en: "", de: "" }, media: [] });

  const save = async () => {
    try {
      const body = { ...draft };
      delete body.id; delete body.created_at; delete body.is_deleted;
      if (draft.id) await api.put(`/admin/${name}/${draft.id}`, body);
      else await api.post(`/admin/${name}`, body);
      toast.success("Kaydedildi"); setDraft(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => { await api.delete(`/admin/${name}/${id}`); load(); toast.success("Silindi"); };
  const textKey = name === "fairs" ? "description" : "content";

  return (
    <div data-testid={`tab-${name}`}>
      <button className={btn} data-testid={`new-${name}-btn`} onClick={() => setDraft(blank())}><Plus size={14} /> Yeni {label}</button>

      {draft && (
        <div className={`${card} mt-6 flex flex-col gap-5`} data-testid={`${name}-form`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Field label={name === "fairs" ? "Fuar Adı" : "Haber Başlığı"} testId={`${name}-title`}
              value={draft[titleField]} onChange={(v) => setDraft({ ...draft, [titleField]: v })} />
            {name === "fairs" && (
              <Field label="Konum" testId="fair-location" value={draft.location} onChange={(v) => setDraft({ ...draft, location: v })} />
            )}
            <Field label="Tarih" testId={`${name}-date`} value={draft.date} onChange={(v) => setDraft({ ...draft, date: v })} />
          </div>
          <LocalizedField label={name === "fairs" ? "Açıklama" : "Haber Metni"} testId={`${name}-text`}
            value={draft[textKey]} onChange={(v) => setDraft({ ...draft, [textKey]: v })} />
          <Uploader label="Fotoğraf & Video" testId={`${name}-media`}
            items={draft.media} onChange={(m) => setDraft({ ...draft, media: m })} />
          <div className="flex gap-3">
            <button onClick={save} className={btn} data-testid={`save-${name}-btn`}><Save size={14} /> Kaydet</button>
            <button onClick={() => setDraft(null)} className={btnGhost}>İptal</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8">
        {items.map((it, i) => (
          <div key={it.id} className={card} data-testid={`admin-${name}-${i}`}>
            <p className="text-[0.6rem] uppercase tracking-[0.2em] text-gold">{it.location} {it.date}</p>
            <p className="font-serif text-2xl text-white mt-1">{it[titleField]}</p>
            <p className="text-xs text-neutral-500 mt-2">{(it.media || []).length} medya</p>
            <div className="flex gap-3 mt-5">
              <button className={btnGhost} data-testid={`edit-${name}-${i}`} onClick={() => setDraft(it)}>Düzenle</button>
              <button className={btnGhost} data-testid={`delete-${name}-${i}`} onClick={() => del(it.id)}><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CatalogsTab() {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState(null);
  const load = () => api.get("/admin/catalogs").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const body = { ...draft };
      delete body.id; delete body.created_at; delete body.is_deleted;
      if (draft.id) await api.put(`/admin/catalogs/${draft.id}`, body);
      else await api.post("/admin/catalogs", body);
      toast.success("Katalog kaydedildi"); setDraft(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => { await api.delete(`/admin/catalogs/${id}`); load(); toast.success("Silindi"); };

  return (
    <div data-testid="tab-catalogs">
      <button className={btn} data-testid="new-catalog-btn"
        onClick={() => setDraft({ title: "", year: "", description: "", cover_url: "", file_url: "" })}>
        <Plus size={14} /> Yeni Katalog
      </button>

      {draft && (
        <div className={`${card} mt-6 flex flex-col gap-5`} data-testid="catalog-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Field label="Katalog Adı" testId="catalog-title" value={draft.title} onChange={(v) => setDraft({ ...draft, title: v })} />
            <Field label="Yıl" testId="catalog-year" value={draft.year} onChange={(v) => setDraft({ ...draft, year: v })} />
            <Field label="Açıklama" testId="catalog-description" value={draft.description} onChange={(v) => setDraft({ ...draft, description: v })} />
          </div>
          <SingleUpload label="Kapak Görseli" accept="image/*" testId="catalog-cover"
            value={draft.cover_url} onChange={(v) => setDraft({ ...draft, cover_url: v })} />
          <SingleUpload label="Katalog Dosyası (PDF)" accept="application/pdf" testId="catalog-file"
            value={draft.file_url} onChange={(v) => setDraft({ ...draft, file_url: v })} />
          <div className="flex gap-3">
            <button onClick={save} className={btn} data-testid="save-catalog-btn"><Save size={14} /> Kaydet</button>
            <button onClick={() => setDraft(null)} className={btnGhost}>İptal</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-8">
        {items.map((c, i) => (
          <div key={c.id} className={card} data-testid={`admin-catalog-${i}`}>
            <p className="text-[0.6rem] uppercase tracking-[0.2em] text-gold">{c.year}</p>
            <p className="font-serif text-2xl text-white mt-1">{c.title}</p>
            <div className="flex gap-3 mt-5">
              <button className={btnGhost} onClick={() => setDraft(c)} data-testid={`edit-catalog-${i}`}>Düzenle</button>
              <button className={btnGhost} onClick={() => del(c.id)} data-testid={`delete-catalog-${i}`}><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function UsersTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ username: "", password: "", company: "" });
  const load = () => api.get("/admin/catalog-users").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/admin/catalog-users", form);
      toast.success("Kullanıcı oluşturuldu");
      setForm({ username: "", password: "", company: "" }); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => { await api.delete(`/admin/catalog-users/${id}`); load(); toast.success("Silindi"); };

  return (
    <div data-testid="tab-users">
      <div className={`${card} flex flex-col gap-5`}>
        <p className="overline">Yeni Katalog Kullanıcısı</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <Field label="Kullanıcı Adı" testId="user-username" value={form.username} onChange={(v) => setForm({ ...form, username: v })} />
          <Field label="Şifre" testId="user-password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} />
          <Field label="Firma" testId="user-company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} />
        </div>
        <button onClick={create} className={`${btn} self-start`} data-testid="create-user-btn"><Plus size={14} /> Oluştur</button>
      </div>

      <div className="mt-8 border border-white/8 overflow-x-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-[#111] text-[0.62rem] uppercase tracking-[0.2em] text-neutral-500">
            <tr><th className="text-left p-4">Kullanıcı</th><th className="text-left p-4">Şifre</th><th className="text-left p-4">Firma</th><th className="p-4"></th></tr>
          </thead>
          <tbody>
            {items.map((u, i) => (
              <tr key={u.id} className="border-t border-white/5" data-testid={`user-row-${i}`}>
                <td className="p-4 text-white">{u.username}</td>
                <td className="p-4 text-neutral-400">{u.plain_hint}</td>
                <td className="p-4 text-neutral-400">{u.company}</td>
                <td className="p-4 text-right">
                  <button onClick={() => del(u.id)} data-testid={`delete-user-${i}`} className="text-neutral-500 hover:text-red-400"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RegistrationsTab() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/registrations").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const del = async (id) => { await api.delete(`/admin/registrations/${id}`); load(); };

  return (
    <div data-testid="tab-registrations" className="border border-white/8 overflow-x-auto">
      <table className="w-full text-sm" data-testid="registrations-table">
        <thead className="bg-[#111] text-[0.62rem] uppercase tracking-[0.2em] text-neutral-500">
          <tr>{["İsim", "E-posta", "Telefon", "Firma", "Ülke / Şehir", "Tarih", ""].map((h) => (
            <th key={h} className="text-left p-4 whitespace-nowrap">{h}</th>))}</tr>
        </thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={7} className="p-8 text-neutral-500">Henüz kayıt talebi yok.</td></tr>}
          {items.map((r, i) => (
            <tr key={r.id} className="border-t border-white/5" data-testid={`registration-row-${i}`}>
              <td className="p-4 text-white whitespace-nowrap">{r.first_name} {r.last_name}</td>
              <td className="p-4 text-neutral-400">{r.email}</td>
              <td className="p-4 text-neutral-400 whitespace-nowrap">{r.phone}</td>
              <td className="p-4 text-neutral-400">{r.company}</td>
              <td className="p-4 text-neutral-400 whitespace-nowrap">{r.country} / {r.city}</td>
              <td className="p-4 text-neutral-500 whitespace-nowrap">{(r.created_at || "").slice(0, 10)}</td>
              <td className="p-4 text-right"><button onClick={() => del(r.id)} data-testid={`delete-registration-${i}`} className="text-neutral-500 hover:text-red-400"><Trash2 size={14} /></button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useApp();
  const [tab, setTab] = useState("content");

  if (!user || user.role !== "admin") return <Login />;

  return (
    <div className="min-h-screen bg-ink" data-testid="admin-panel">
      <header className="border-b border-white/10 bg-black/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-[1600px] mx-auto px-6 h-24 flex items-center justify-between gap-6">
          <div className="flex items-center gap-6">
            <img src={LOGO} alt="BEDAS" className="h-12 w-auto object-contain logo-light" />
            <span className="hidden sm:inline overline">Yönetim Paneli</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className={btnGhost} data-testid="admin-back-site"><ArrowLeft size={13} /> Site</Link>
            <button onClick={logout} className={btnGhost} data-testid="admin-logout-btn"><LogOut size={13} /> Çıkış</button>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-6 py-10">
        <div className="flex gap-2 flex-wrap mb-10 border-b border-white/8 pb-4">
          {TABS.map(([k, label]) => (
            <button key={k} data-testid={`tab-btn-${k}`} onClick={() => setTab(k)}
              className={`px-5 py-2.5 text-[0.66rem] uppercase tracking-[0.2em] border transition-colors duration-300 ${
                tab === k ? "border-gold text-gold" : "border-transparent text-neutral-500 hover:text-white"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "content" && <ContentTab />}
        {tab === "products" && <ProductsTab />}
        {tab === "catalogs" && <CatalogsTab />}
        {tab === "users" && <UsersTab />}
        {tab === "fairs" && <MediaCollectionTab name="fairs" titleField="fair_name" label="Fuar" />}
        {tab === "news" && <MediaCollectionTab name="news" titleField="title" label="Haber" />}
        {tab === "registrations" && <RegistrationsTab />}
      </div>
    </div>
  );
}
